"use client";

import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/navigation";
import { Link } from "@/navigation";
import Image from "next/image";
import { useState } from "react";

export default function Navbar({ locale }: { locale: string }) {
  const t = useTranslations("nav");
  const pathname = usePathname(); // ruta sin prefijo de locale
  const router   = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);

  const otherLocale = locale === "es" ? "en" : "es";

  function switchLocale() {
    router.replace(pathname, { locale: otherLocale });
  }

  const links = [
    { href: "/",          label: t("home")     },
    { href: "/servicios", label: t("services")  },
    { href: "/sobre-mi",  label: t("about")     },
    { href: "/blog",      label: t("blog")      },
    { href: "/contacto",  label: t("contact")   },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#09090b]/90 backdrop-blur-sm border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Image
              src="/logo/logo.svg"
              alt="DC Solutions"
              width={64}
              height={32}
              className="opacity-90 hover:opacity-100 transition-opacity"
            />
          </Link>

          {/* Nav desktop */}
          <nav className="hidden md:flex items-center gap-6">
            {links.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                className={`text-sm font-medium transition-colors hover:text-cyan-400 ${
                  pathname === href ? "text-cyan-400" : "text-gray-300"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Switcher de idioma */}
            <button
              onClick={switchLocale}
              className="text-xs font-semibold text-gray-400 hover:text-white border border-white/10 hover:border-white/30 px-2.5 py-1 rounded transition-colors uppercase tracking-wider"
            >
              {otherLocale === "en" ? "EN" : "ES"}
            </button>

            {/* CTA desktop */}
            <Link
              href="/contacto"
              className="hidden md:inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-[#09090b] text-sm font-bold px-4 py-2 rounded-lg transition-colors"
            >
              {t("contact")}
            </Link>

            {/* Hamburger */}
            <button
              className="md:hidden text-gray-300 hover:text-white"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Nav mobile */}
        {menuOpen && (
          <nav className="md:hidden pb-4 flex flex-col gap-1">
            {links.map(({ href, label }) => (
              <Link
                key={href}
                href={href}
                onClick={() => setMenuOpen(false)}
                className={`text-sm font-medium px-3 py-2.5 rounded-lg transition-colors hover:text-cyan-400 hover:bg-white/[0.04] ${
                  pathname === href ? "text-cyan-400" : "text-gray-300"
                }`}
              >
                {label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
